import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Upload,
  Search,
  Download,
  Trash2,
  Edit,
  File,
  FileText,
  FileVideo,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const MediaPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Upload dialog
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadKeywords, setUploadKeywords] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [editKeywords, setEditKeywords] = useState("");
  const [editDescription, setEditDescription] = useState("");
  
  // Detail view dialog
  const [detailDialog, setDetailDialog] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, [page, searchTerm, fileTypeFilter]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "100",
      });
      
      if (searchTerm) params.append("search", searchTerm);
      if (fileTypeFilter) params.append("file_type", fileTypeFilter);
      
      const response = await axios.get(`${API}/media?${params.toString()}`);
      setMediaFiles(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch media:", error);
      toast.error("Medien konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Bitte wählen Sie eine Datei aus");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("keywords", uploadKeywords);
      formData.append("description", uploadDescription);

      await axios.post(`${API}/media/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Datei erfolgreich hochgeladen");
      setUploadDialog(false);
      setSelectedFile(null);
      setUploadKeywords("");
      setUploadDescription("");
      fetchMedia();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingMedia) return;

    try {
      const keywords = editKeywords.split(",").map(k => k.trim()).filter(k => k);
      
      await axios.put(`${API}/media/${editingMedia.id}`, {
        keywords,
        description: editDescription,
      });

      toast.success("Metadaten aktualisiert");
      setEditDialog(false);
      setEditingMedia(null);
      fetchMedia();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Aktualisierung fehlgeschlagen");
    }
  };

  const handleDelete = async (mediaId, filename) => {
    if (!window.confirm(`Möchten Sie "${filename}" wirklich löschen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/media/${mediaId}`);
      toast.success("Datei gelöscht");
      fetchMedia();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const openEditDialog = (media) => {
    setEditingMedia(media);
    setEditKeywords(media.keywords.join(", "));
    setEditDescription(media.description || "");
    setEditDialog(true);
  };

  const openDetailDialog = (media) => {
    setViewingMedia(media);
    setDetailDialog(true);
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "image":
        return <ImageIcon className="w-6 h-6" />;
      case "video":
        return <FileVideo className="w-6 h-6" />;
      case "pdf":
        return <FileText className="w-6 h-6" />;
      default:
        return <File className="w-6 h-6" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setUploadDialog(true);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Media Verwaltung</h1>
            <p className="text-slate-600 mt-1">
              Verwalten Sie Ihre Medien-Dateien - Bilder, Videos, PDFs und Dokumente
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>

        {/* Upload Area */}
        <Card
          className="mb-6 border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 transition-colors cursor-pointer"
          onClick={() => setUploadDialog(true)}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <CardContent className="p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Dateien hochladen
            </h3>
            <p className="text-slate-600">
              Klicken Sie hier oder ziehen Sie Dateien in diesen Bereich
            </p>
          </CardContent>
        </Card>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Suchen in Dateinamen, Keywords, Beschreibung..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>
          <select
            value={fileTypeFilter}
            onChange={(e) => {
              setFileTypeFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg bg-white"
          >
            <option value="">Alle Dateitypen</option>
            <option value="image">Bilder</option>
            <option value="video">Videos</option>
            <option value="pdf">PDFs</option>
            <option value="document">Dokumente</option>
          </select>
        </div>

        {/* Media Grid */}
        {mediaFiles.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Keine Medien gefunden
              </h3>
              <p className="text-slate-600">
                Laden Sie Ihre erste Datei hoch, um zu beginnen
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
              {mediaFiles.map((media) => (
                <Card
                  key={media.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div
                    className="aspect-square bg-slate-100 flex items-center justify-center relative group"
                    onClick={() => openDetailDialog(media)}
                  >
                    {media.thumbnail_url ? (
                      <img
                        src={media.thumbnail_url}
                        alt={media.original_filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-slate-400">
                        {getFileIcon(media.file_type)}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all" />
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium text-slate-900 truncate mb-1">
                      {media.original_filename}
                    </p>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {media.file_type}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {formatFileSize(media.file_size)}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(media.hetzner_url, "_blank");
                        }}
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(media);
                        }}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 flex-1 text-red-600 hover:text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(media.id, media.original_filename);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Seite {page} von {totalPages} ({total} Dateien)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Zurück
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Weiter
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Datei hochladen</DialogTitle>
              <DialogDescription>
                Laden Sie eine Mediendatei hoch und fügen Sie Metadaten hinzu
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Datei</label>
                <Input
                  type="file"
                  onChange={handleFileSelect}
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
                {selectedFile && (
                  <p className="text-sm text-slate-600 mt-2">
                    Ausgewählt: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Keywords (kommagetrennt)
                </label>
                <Input
                  value={uploadKeywords}
                  onChange={(e) => setUploadKeywords(e.target.value)}
                  placeholder="z.B. Produkt, Dokumentation, 2024"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Beschreibung</label>
                <Textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optionale Beschreibung der Datei"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
                {uploading ? "Wird hochgeladen..." : "Hochladen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Metadaten bearbeiten</DialogTitle>
              <DialogDescription>
                {editingMedia?.original_filename}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Keywords (kommagetrennt)
                </label>
                <Input
                  value={editKeywords}
                  onChange={(e) => setEditKeywords(e.target.value)}
                  placeholder="z.B. Produkt, Dokumentation, 2024"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Beschreibung</label>
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Optionale Beschreibung der Datei"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleEdit}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail View Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{viewingMedia?.original_filename}</DialogTitle>
              <DialogDescription>
                {viewingMedia?.file_type} • {formatFileSize(viewingMedia?.file_size || 0)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Preview */}
              <div className="bg-slate-100 rounded-lg flex items-center justify-center p-4">
                {viewingMedia?.file_type === "image" ? (
                  <img
                    src={viewingMedia.hetzner_url}
                    alt={viewingMedia.original_filename}
                    className="max-h-96 object-contain"
                  />
                ) : (
                  <div className="text-slate-400 py-12">
                    {getFileIcon(viewingMedia?.file_type)}
                    <p className="mt-4 text-sm">Keine Vorschau verfügbar</p>
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className="space-y-3">
                {viewingMedia?.keywords && viewingMedia.keywords.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Keywords</label>
                    <div className="flex flex-wrap gap-2">
                      {viewingMedia.keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {viewingMedia?.description && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Beschreibung</label>
                    <p className="text-sm text-slate-700">{viewingMedia.description}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">Details</label>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p>Hochgeladen von: {viewingMedia?.uploaded_by}</p>
                    <p>
                      Datum:{" "}
                      {viewingMedia?.uploaded_at &&
                        new Date(viewingMedia.uploaded_at).toLocaleString("de-DE")}
                    </p>
                    <p>MIME-Type: {viewingMedia?.mime_type}</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => window.open(viewingMedia?.hetzner_url, "_blank")}
              >
                <Download className="w-4 h-4 mr-2" />
                Herunterladen
              </Button>
              <Button onClick={() => setDetailDialog(false)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MediaPage;
